import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTradingCall } from '../contexts/TradingCallContext';
import { useLanguage } from '../contexts/LanguageContext';
import { TradingCall, TradingCallConfig, PortfolioCallLayer } from '../types/TradingCall';
import type { ScannerData } from '../types/ScannerData';
import { getApiUrl } from '../config/api';
import { getAuthToken } from '../utils/authUtils';
import { MMEC_BRAND_COLORS } from '../utils/mmecBrandColors';
import { formatMoney, formatLegLines } from '../utils/paperTradingFormat';
import { isDesignSystemOn } from '../theme/mmecDsPreview';
import './TradingCallSystem.ds.css';

type AiStrategyRec = {
  id: string;
  ts?: string;
  symbol: string;
  strategy_type: string;
  source?: string;
  outcome?: string | null;
  pnl?: number | null;
  r_multiple?: number | null;
  target?: string;
  stop?: string;
};

interface TradingCallSystemProps {
  fullScreen?: boolean;
  /** Live scanner rows from App — used for manual refresh of rule-based calls */
  scannerData?: ScannerData[];
}

function EquitySparkline({
  history,
  stroke,
}: {
  history: { equity: number }[];
  stroke: string;
}) {
  const pts = useMemo(() => {
    if (history.length < 2) return [];
    const maxPts = 80;
    const step = history.length > maxPts ? Math.ceil(history.length / maxPts) : 1;
    const sampled: { equity: number }[] = [];
    for (let i = 0; i < history.length; i += step) sampled.push(history[i]);
    if (sampled[sampled.length - 1] !== history[history.length - 1]) sampled.push(history[history.length - 1]);
    return sampled;
  }, [history]);
  if (pts.length < 2) return <span className="text-xs opacity-60">—</span>;
  const vals = pts.map((p) => p.equity);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const w = 200;
  const h = 48;
  const pad = 4;
  const range = max - min || 1;
  const d = pts
    .map((p, i) => {
      const x = pad + (i / (pts.length - 1)) * (w - 2 * pad);
      const y = pad + (1 - (p.equity - min) / range) * (h - 2 * pad);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} className="block" aria-hidden>
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

const TradingCallSystem: React.FC<TradingCallSystemProps> = ({ fullScreen = false, scannerData = [] }) => {
  const { 
    config, 
    updateConfig, 
    activeCalls, 
    closedCalls, 
    generateCalls, 
    refreshAllCallPerformance,
    setVirtualStartingCapital,
    wallet,
    exportWalletData,
    importWalletData,
    resetPaperWallet
  } = useTradingCall();
  
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState<'config' | 'calls' | 'aiPortfolio' | 'wallet' | 'performance'>('wallet');
  const [startingCapitalInput, setStartingCapitalInput] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [tradingStyle, setTradingStyle] = useState<'aggressive' | 'moderate' | 'conservative' | 'specific'>('moderate');
  const [callLayerFilter, setCallLayerFilter] = useState<'all' | PortfolioCallLayer>('all');

  const isArabic = language === 'ar';
  // Design System preview: tcs-* hooks below are styled only by TradingCallSystem.ds.css (scoped to the flag).
  const ds = isDesignSystemOn();
  const textClass = 'text-[color:var(--txt)]';
  const bgClass = 'bg-[color:var(--elev)]';
  const borderClass = 'border-[color:var(--hair)]';

  const translations = {
    title: isArabic ? 'تداول تجريبي' : 'Paper Trading',
    subtitle: isArabic
      ? 'محفظة افتراضية برأس مال ورقي — يُحدَّث المركز عند توفر بيانات الماسح (تقييم بالأسعار).'
      : 'Virtual portfolio with paper capital — positions are marked to market when scanner data refreshes.',
    config: isArabic ? 'الإعدادات' : 'Configuration',
    calls: isArabic ? 'الفرص' : 'Trading Calls',
    wallet: isArabic ? 'المحفظة' : 'Wallet',
    performance: isArabic ? 'الأداء' : 'Performance',
    enabled: isArabic ? 'مفعل' : 'Enabled',
    preferredTickers: isArabic ? 'الرموز المفضلة' : 'Preferred Tickers',
    callTypes: isArabic ? 'أنواع الفرص' : 'Call Types',
    strategies: isArabic ? 'الاستراتيجيات' : 'Strategies',
    riskManagement: isArabic ? 'إدارة المخاطر' : 'Risk Management',
    sentimentThresholds: isArabic ? 'حدود المشاعر' : 'Sentiment Thresholds',
    futures: isArabic ? 'العقود الآجلة' : 'Futures',
    options: isArabic ? 'الخيارات' : 'Options',
    nakedCalls: isArabic ? 'خيارات شراء عارية' : 'Naked Calls',
    nakedPuts: isArabic ? 'خيارات بيع عارية' : 'Naked Puts',
    verticalSpreads: isArabic ? 'فروق رأسية' : 'Vertical Spreads',
    complexSpreads: isArabic ? 'فروق معقدة' : 'Complex Spreads',
    maxPositionSize: isArabic ? 'الحد الأقصى لحجم المركز' : 'Max Position Size',
    maxRiskPerTrade: isArabic ? 'الحد الأقصى للمخاطرة لكل صفقة' : 'Max Risk Per Trade',
    defaultStopLoss: isArabic ? 'وقف الخسارة الافتراضي' : 'Default Stop Loss',
    defaultTarget: isArabic ? 'الهدف الافتراضي' : 'Default Target',
    bullish: isArabic ? 'صاعد' : 'Bullish',
    bearish: isArabic ? 'هابط' : 'Bearish',
    confidence: isArabic ? 'الثقة' : 'Confidence',
    activeCalls: isArabic ? 'الفرص النشطة' : 'Active Calls',
    closedCalls: isArabic ? 'الفرص المغلقة' : 'Closed Calls',
    balance: isArabic ? 'الرصيد' : 'Balance',
    totalPnL: isArabic ? 'إجمالي الربح/الخسارة' : 'Total P&L',
    winRate: isArabic ? 'معدل الفوز' : 'Win Rate',
    totalTrades: isArabic ? 'إجمالي الصفقات' : 'Total Trades',
    export: isArabic ? 'تصدير' : 'Export',
    import: isArabic ? 'استيراد' : 'Import',
    generate: isArabic ? 'إنشاء فرص' : 'Generate Calls',
    refresh: isArabic ? 'تحديث' : 'Refresh',
    noData: isArabic ? 'لا توجد بيانات متاحة. يرجى الانتظار لتحميل البيانات.' : 'No market data available. Please wait for data to load.',
    aiPortfolio: isArabic ? 'استراتيجيات مولدة' : 'AI Strategies',
    aiStrategiesRefresh: isArabic ? 'تحديث القائمة' : 'Refresh list',
    aiStrategiesEmpty: isArabic ? 'لا توجد سجلات بعد. استخدم مولد الإشارات لإنشاء استراتيجيات.' : 'No saved strategies yet. Use Trading Call Generator to create strategies.',
    aiStrategiesLoading: isArabic ? 'جاري التحميل…' : 'Loading…',
    aiStrategiesNote: isArabic
      ? 'الاستراتيجيات المحفوظة من المولد تظهر هنا. صفوف المحفظة أدناه تعتمد على بيانات الماسح عند التحديث.'
      : 'Strategies saved from the generator appear here. Wallet rows below use scanner-driven signals when data refreshes.',
    resetPaper: isArabic ? 'إعادة تعيين الحساب التجريبي' : 'Reset paper account',
    resetPaperConfirm: isArabic
      ? 'مسح كل المحفظة التجريبة والمحفوظات محلياً؟'
      : 'Clear all paper positions and local saved wallet?',
    virtualPortfolio: isArabic ? 'محفظة افتراضية' : 'Virtual portfolio',
    startingCapital: isArabic ? 'رأس المال الافتتاحي ($)' : 'Starting capital ($)',
    currentEquity: isArabic ? 'قيمة المحفظة الحالية' : 'Current equity',
    totalReturn: isArabic ? 'العائد الإجمالي' : 'Total return',
    unrealized: isArabic ? 'ربح/خسارة غير محققة' : 'Unrealized P&L',
    realized: isArabic ? 'ربح/خسارة محققة' : 'Realized P&L',
    equityCurve: isArabic ? 'منحنى رأس المال' : 'Equity curve',
    applyCapital: isArabic ? 'تطبيق' : 'Apply',
    capitalHint: isArabic
      ? 'يُحدّث مرجع العائد فقط؛ المراكز الحالية لا تُغيّر.'
      : 'Updates the return benchmark; open positions are unchanged.',
    callLayer: isArabic ? 'طبقة الفرص' : 'Call layer',
    layerAll: isArabic ? 'الكل' : 'All',
    layerPulse: isArabic ? 'تقرير النبض' : 'Pulse report',
    layerPlatform: isArabic ? 'المنصة' : 'Platform',
    debit: isArabic ? 'المدفوع (علاوة)' : 'Debit (premium)',
    mark: isArabic ? 'تسعير حالي' : 'Mark',
    tp: isArabic ? 'هدف (علاوة)' : 'Take-profit (mark)',
    sl: isArabic ? 'وقف (علاوة)' : 'Stop (mark)',
    underlyingRef: isArabic ? 'مرجع السعر للأصل' : 'Underlying reference',
    markUpdated: isArabic ? 'آخر تحديث للتسعير' : 'Mark updated',
    legs: isArabic ? 'الأرجل' : 'Legs',
    opened: isArabic ? 'فُتحت' : 'Opened',
  };

  const filteredActiveCalls = useMemo(() => {
    if (callLayerFilter === 'all') return activeCalls;
    return activeCalls.filter((c) => (c.portfolioLayer ?? 'platform') === callLayerFilter);
  }, [activeCalls, callLayerFilter]);

  const filteredClosedCalls = useMemo(() => {
    if (callLayerFilter === 'all') return closedCalls;
    return closedCalls.filter((c) => (c.portfolioLayer ?? 'platform') === callLayerFilter);
  }, [closedCalls, callLayerFilter]);

  const callLayerBadge = useCallback(
    (call: TradingCall) => {
      const layer = call.portfolioLayer ?? 'platform';
      if (ds) {
        const src0 = call.platformSource ?? 'scanner';
        const label =
          layer === 'elite_pulse' ? (isArabic ? 'نبض' : 'Pulse')
            : src0 === 'ollama' ? (isArabic ? 'ذكاء' : 'AI')
              : src0 === 'manual' ? (isArabic ? 'يدوي' : 'Manual')
                : (isArabic ? 'منصّة' : 'Scanner');
        return { label, className: layer === 'elite_pulse' ? 'mmec-tag mmec-tag--gold' : 'mmec-tag' };
      }
      if (layer === 'elite_pulse') {
        return {
          label: isArabic ? 'نبض' : 'Pulse',
          className: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200',
        };
      }
      const src = call.platformSource ?? 'scanner';
      if (src === 'ollama') {
        return {
          label: isArabic ? 'ذكاء' : 'AI',
          className: 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200',
        };
      }
      if (src === 'manual') {
        return {
          label: isArabic ? 'يدوي' : 'Manual',
          className: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
        };
      }
      return {
        label: isArabic ? 'منصّة' : 'Scanner',
        className: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      };
    },
    [isArabic, ds],
  );

  const [aiRecs, setAiRecs] = useState<AiStrategyRec[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const loadAiStrategies = useCallback(async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(
        getApiUrl('/api/ai/strategy-recommendations?limit=100&source=trading_call_generator'),
        { headers }
      );
      const data = await res.json();
      if (!res.ok) {
        setAiError(typeof data.error === 'string' ? data.error : 'Request failed');
        setAiRecs([]);
        return;
      }
      setAiRecs((data.recommendations || []) as AiStrategyRec[]);
    } catch (e) {
      setAiError(e instanceof Error ? e.message : 'Network error');
      setAiRecs([]);
    } finally {
      setAiLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'aiPortfolio') loadAiStrategies();
  }, [activeTab, loadAiStrategies]);

  useEffect(() => {
    setStartingCapitalInput(String(Math.round(wallet.initialBalance ?? 100000)));
  }, [wallet.initialBalance]);

  const portfolioMetrics = useMemo(() => {
    const initial = wallet.initialBalance ?? 100000;
    const unrealized = activeCalls.reduce((s, c) => s + (c.performance?.pnl || 0), 0);
    const realized = closedCalls.reduce((s, c) => s + (c.performance?.pnl || 0), 0);
    const totalReturnPct = initial > 0 ? ((wallet.balance - initial) / initial) * 100 : 0;
    return { initial, unrealized, realized, totalReturnPct };
  }, [wallet.initialBalance, wallet.balance, activeCalls, closedCalls]);

  const handleConfigChange = (key: keyof TradingCallConfig, value: any) => {
    updateConfig({ [key]: value });
  };

  const handleNestedConfigChange = (parentKey: keyof TradingCallConfig, childKey: string, value: any) => {
    updateConfig({
      [parentKey]: {
        ...(config[parentKey] as any),
        [childKey]: value
      }
    });
  };

  const handleExport = () => {
    const data = exportWalletData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trading-wallet-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target?.result as string;
        importWalletData(data);
      };
      reader.readAsText(file);
    }
  };

  const getStatusColor = (status: string) => {
    if (ds) {
      if (status === 'TARGET_HIT') return 'tcs-st-up';
      if (status === 'STOP_LOSS') return 'tcs-st-down';
      if (status === 'ACTIVE') return 'tcs-st-info';
      return 'tcs-st-muted';
    }
    switch (status) {
      case 'ACTIVE': return 'text-green-500';
      case 'TARGET_HIT': return 'text-blue-500';
      case 'STOP_LOSS': return 'text-red-500';
      case 'EXPIRED': return 'text-gray-500';
      case 'CANCELLED': return 'text-yellow-500';
      case 'MANUAL_CLOSE': return 'text-purple-500';
      default: return 'text-gray-400';
    }
  };

  const getPnLColor = (pnl: number) => {
    return pnl >= 0 ? 'text-[color:var(--up)]' : 'text-[color:var(--down)]';
  };

  return (
    <div className={`p-6 ${bgClass} rounded-[10px] ${borderClass} border tcs-root`} dir={ds ? (isArabic ? 'rtl' : 'ltr') : undefined}>
      {/* Header */}
      <div className="flex flex-col gap-2 mb-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className={`text-[18px] font-semibold ${textClass}`}>{translations.title}</h2>
          <p className={`text-xs mt-1 max-w-xl text-[color:var(--muted)]`}>{translations.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={tradingStyle}
            onChange={(e) => setTradingStyle(e.target.value as 'aggressive' | 'moderate' | 'conservative' | 'specific')}
            className="px-3 py-1.5 rounded-lg border text-[13px] border-[color:var(--border)] bg-[color:var(--elev)] text-[color:var(--txt)] tcs-ctl"
          >
            <option value="aggressive">{isArabic ? 'عدواني' : 'Aggressive'}</option>
            <option value="moderate">{isArabic ? 'معتدل' : 'Moderate'}</option>
            <option value="conservative">{isArabic ? 'محافظ' : 'Conservative'}</option>
            <option value="specific">{isArabic ? 'استراتيجية محددة' : 'Specific Strategy'}</option>
          </select>
          <button
            onClick={() => setShowConfig(!showConfig)}
            className="px-3 py-1.5 rounded-lg text-[13px] font-medium bg-[color:var(--acc)] text-[color:var(--void)] transition-opacity hover:opacity-90 tcs-btn"
          >
            {showConfig ? '✕' : '⚙️'} {translations.config}
          </button>
          <button
            type="button"
            onClick={() => {
              const rows = scannerData.length ? scannerData : [];
              generateCalls(rows);
              refreshAllCallPerformance(rows);
            }}
            title={scannerData.length === 0 ? translations.noData : undefined}
            className="px-3 py-1.5 rounded-lg text-[13px] font-medium bg-[color:var(--up)] text-[color:var(--void)] transition-opacity hover:opacity-90 disabled:opacity-50 tcs-primary"
            disabled={scannerData.length === 0}
          >
            🔄 {translations.generate}
          </button>
        </div>
      </div>

      {/* Configuration Panel */}
      {showConfig && (
        <div className={`mb-6 p-4 bg-[color:var(--elev2)] rounded-lg`}>
          <h3 className={`text-[14px] font-semibold mb-4 ${textClass}`}>{translations.config}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Enable/Disable */}
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => handleConfigChange('enabled', e.target.checked)}
                className="mr-2"
              />
              <label className={textClass}>{translations.enabled}</label>
            </div>

            {/* Preferred Tickers */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${textClass}`}>
                {translations.preferredTickers}
              </label>
              <input
                type="text"
                value={config.preferredTickers.join(', ')}
                onChange={(e) => handleConfigChange('preferredTickers', e.target.value.split(', ').filter(Boolean))}
                className={`w-full p-2 rounded border ${borderClass} bg-[color:var(--elev)] text-[color:var(--txt)]`}
                placeholder="SPY, QQQ, GLD"
              />
            </div>

            {/* Call Types */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${textClass}`}>
                {translations.callTypes}
              </label>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.callTypes.futures}
                    onChange={(e) => handleNestedConfigChange('callTypes', 'futures', e.target.checked)}
                    className="mr-2"
                  />
                  <label className={textClass}>{translations.futures}</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.callTypes.options}
                    onChange={(e) => handleNestedConfigChange('callTypes', 'options', e.target.checked)}
                    className="mr-2"
                  />
                  <label className={textClass}>{translations.options}</label>
                </div>
              </div>
            </div>

            {/* Strategies */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${textClass}`}>
                {translations.strategies}
              </label>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.strategies.nakedCalls}
                    onChange={(e) => handleNestedConfigChange('strategies', 'nakedCalls', e.target.checked)}
                    className="mr-2"
                  />
                  <label className={textClass}>{translations.nakedCalls}</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.strategies.nakedPuts}
                    onChange={(e) => handleNestedConfigChange('strategies', 'nakedPuts', e.target.checked)}
                    className="mr-2"
                  />
                  <label className={textClass}>{translations.nakedPuts}</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.strategies.verticalSpreads}
                    onChange={(e) => handleNestedConfigChange('strategies', 'verticalSpreads', e.target.checked)}
                    className="mr-2"
                  />
                  <label className={textClass}>{translations.verticalSpreads}</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.strategies.complexSpreads}
                    onChange={(e) => handleNestedConfigChange('strategies', 'complexSpreads', e.target.checked)}
                    className="mr-2"
                  />
                  <label className={textClass}>{translations.complexSpreads}</label>
                </div>
              </div>
            </div>

            {/* Risk Management */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${textClass}`}>
                {translations.riskManagement}
              </label>
              <div className="space-y-2">
                <div>
                  <label className={`block text-xs ${textClass}`}>{translations.maxPositionSize}</label>
                  <input
                    type="number"
                    value={config.riskManagement.maxPositionSize}
                    onChange={(e) => handleNestedConfigChange('riskManagement', 'maxPositionSize', Number(e.target.value))}
                    className={`w-full p-1 rounded border ${borderClass} bg-[color:var(--elev)] text-[color:var(--txt)]`}
                  />
                </div>
                <div>
                  <label className={`block text-xs ${textClass}`}>{translations.maxRiskPerTrade}</label>
                  <input
                    type="number"
                    value={config.riskManagement.maxRiskPerTrade}
                    onChange={(e) => handleNestedConfigChange('riskManagement', 'maxRiskPerTrade', Number(e.target.value))}
                    className={`w-full p-1 rounded border ${borderClass} bg-[color:var(--elev)] text-[color:var(--txt)]`}
                  />
                </div>
              </div>
            </div>

            {/* Sentiment Thresholds */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${textClass}`}>
                {translations.sentimentThresholds}
              </label>
              <div className="space-y-2">
                <div>
                  <label className={`block text-xs ${textClass}`}>{translations.confidence}</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={config.sentimentThresholds.confidence}
                    onChange={(e) => handleNestedConfigChange('sentimentThresholds', 'confidence', Number(e.target.value))}
                    className={`w-full p-1 rounded border ${borderClass} bg-[color:var(--elev)] text-[color:var(--txt)]`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-[color:var(--hair)] mb-4">
        {(['config', 'calls', 'aiPortfolio', 'wallet', 'performance'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-2 text-[13px] font-medium transition-colors tcs-tab${activeTab === tab ? ' tcs-tab--on' : ''} ${
              activeTab === tab
                ? 'text-[color:var(--acc)] border-b-2 border-[color:var(--acc)]'
                : 'text-[color:var(--muted)] hover:text-[color:var(--txt)]'
            }`}
          >
            {translations[tab]}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={`${fullScreen ? 'h-full' : 'min-h-[400px]'}`}>
        {activeTab === 'calls' && (
          <div className="space-y-4">
            {/* Active Calls */}
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h3 className={`text-[14px] font-semibold ${textClass}`}>
                  {translations.activeCalls} ({filteredActiveCalls.length}
                  {callLayerFilter !== 'all' && activeCalls.length !== filteredActiveCalls.length
                    ? ` / ${activeCalls.length}`
                    : ''}
                  )
                </h3>
                <div className="flex items-center gap-2">
                  <label className={`text-xs whitespace-nowrap ${textClass}`} htmlFor="paper-call-layer-filter">
                    {translations.callLayer}
                  </label>
                  <select
                    id="paper-call-layer-filter"
                    value={callLayerFilter}
                    onChange={(e) => setCallLayerFilter(e.target.value as 'all' | PortfolioCallLayer)}
                    className={`text-xs rounded border px-2 py-1 min-w-[8rem] ${borderClass} ${bgClass} ${textClass}`}
                  >
                    <option value="all">{translations.layerAll}</option>
                    <option value="elite_pulse">{translations.layerPulse}</option>
                    <option value="platform">{translations.layerPlatform}</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                {filteredActiveCalls.map((call) => {
                  const badge = callLayerBadge(call);
                  const legLbl =
                    isArabic
                      ? ({ long: 'شراء', short: 'بيع', call: 'خيار الشراء', put: 'خيار البيع' } as const)
                      : undefined;
                  const legLines = formatLegLines(call, legLbl);
                  return (
                    <div key={call.id} className={`p-3 rounded-lg border ${borderClass} bg-[color:var(--elev2)] tcs-card`}>
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold flex items-center gap-2 flex-wrap">
                            <span>{call.symbol}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.className}`}>{badge.label}</span>
                          </div>
                          <div className="text-xs text-[color:var(--muted)]">
                            {call.strategy.type} • {call.direction} • Confidence: {call.confidence}%
                          </div>
                          <div className="text-sm space-y-1">
                            <div>
                              {translations.debit}: ${formatMoney(call.entryPrice)}
                              {call.performance?.currentPrice != null && (
                                <>
                                  {' '}
                                  · {translations.mark}: ${formatMoney(call.performance.currentPrice)}
                                </>
                              )}
                            </div>
                            <div>
                              {translations.tp}: ${formatMoney(call.targetPrice)} · {translations.sl}: $
                              {formatMoney(call.stopLoss)}
                            </div>
                            {call.strategy.details.targetUnderlyingPrice != null &&
                              call.strategy.details.stopUnderlyingPrice != null && (
                                <div className={`text-xs text-[color:var(--muted)]`}>
                                  {translations.underlyingRef}: ~
                                  {formatMoney(call.strategy.details.targetUnderlyingPrice)} / ~
                                  {formatMoney(call.strategy.details.stopUnderlyingPrice)}
                                </div>
                              )}
                            {legLines.length > 0 && (
                              <div className={`text-xs text-[color:var(--txt-2)]`}>
                                <span className="font-medium">{translations.legs}: </span>
                                {legLines.join(' · ')}
                              </div>
                            )}
                            {call.performance?.lastUpdate && (
                              <div className={`text-xs text-[color:var(--muted-2)]`}>
                                {translations.markUpdated}:{' '}
                                {new Date(call.performance.lastUpdate).toLocaleString()}
                              </div>
                            )}
                            <div className={`text-xs text-[color:var(--muted-2)]`}>
                              {translations.opened}: {new Date(call.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className={`text-right shrink-0 ${getStatusColor(call.status)}`}>
                          <div className="font-semibold">{call.status}</div>
                          {call.performance && (
                            <div className={`text-sm ${getPnLColor(call.performance.pnl)}`}>
                              P&L: ${call.performance.pnl.toFixed(2)} ({call.performance.pnlPercentage.toFixed(1)}%)
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {activeCalls.length === 0 && (
                  <div className={`text-center py-8 text-gray-500 ${textClass}`}>
                    No active trading calls
                  </div>
                )}
                {activeCalls.length > 0 && filteredActiveCalls.length === 0 && (
                  <div className={`text-center py-8 text-gray-500 ${textClass}`}>
                    {isArabic ? 'لا توجد فرص في هذه الطبقة.' : 'No calls in this layer.'}
                  </div>
                )}
              </div>
            </div>

            {/* Closed Calls */}
            <div>
              <h3 className={`text-[14px] font-semibold mb-3 ${textClass}`}>
                {translations.closedCalls} ({filteredClosedCalls.length}
                {callLayerFilter !== 'all' && closedCalls.length !== filteredClosedCalls.length
                  ? ` / ${closedCalls.length}`
                  : ''}
                )
              </h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredClosedCalls.slice(0, 10).map((call) => {
                  const badge = callLayerBadge(call);
                  return (
                    <div key={call.id} className={`p-3 rounded-lg border ${borderClass} bg-[color:var(--elev2)] tcs-card`}>
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold flex items-center gap-2 flex-wrap">
                            <span>{call.symbol}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.className}`}>{badge.label}</span>
                          </div>
                          <div className="text-xs text-[color:var(--muted)]">
                            {call.strategy.type} • {call.direction} • {call.status}
                          </div>
                        </div>
                        <div className={`text-right shrink-0 ${getPnLColor(call.performance?.pnl || 0)}`}>
                          {call.performance && (
                            <div className="font-semibold">
                              ${call.performance.pnl.toFixed(2)} ({call.performance.pnlPercentage.toFixed(1)}%)
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'aiPortfolio' && (
          <div className="space-y-4">
            <p className={`text-sm text-[color:var(--muted)]`}>{translations.aiStrategiesNote}</p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => loadAiStrategies()}
                disabled={aiLoading}
                className="px-3 py-1.5 rounded-lg text-[13px] font-medium bg-[color:var(--acc)] text-[color:var(--void)] transition-opacity hover:opacity-90 disabled:opacity-50 tcs-btn"
              >
                {aiLoading ? translations.aiStrategiesLoading : translations.aiStrategiesRefresh}
              </button>
            </div>
            {aiError && (
              <div
                className="text-[12.5px] rounded-lg px-3 py-2"
                style={{ background: 'var(--down-dim)', color: 'var(--down)', border: '1px solid color-mix(in srgb, var(--down) 40%, transparent)' }}
              >
                {aiError}
              </div>
            )}
            <div className="overflow-x-auto rounded-lg border border-[color:var(--hair)] tcs-tablewrap">
              <table className="min-w-full text-[12.5px]">
                <thead className="bg-[color:var(--elev)]">
                  <tr>
                    <th className="px-2.5 py-1.5 text-start text-[11px] font-semibold tracking-[.03em] text-[color:var(--muted)]">Time</th>
                    <th className="px-2.5 py-1.5 text-start text-[11px] font-semibold tracking-[.03em] text-[color:var(--muted)]">Symbol</th>
                    <th className="px-2.5 py-1.5 text-start text-[11px] font-semibold tracking-[.03em] text-[color:var(--muted)]">Strategy</th>
                    <th className="px-2.5 py-1.5 text-start text-[11px] font-semibold tracking-[.03em] text-[color:var(--muted)]">Target</th>
                    <th className="px-2.5 py-1.5 text-start text-[11px] font-semibold tracking-[.03em] text-[color:var(--muted)]">Stop</th>
                    <th className="px-2.5 py-1.5 text-start text-[11px] font-semibold tracking-[.03em] text-[color:var(--muted)]">Outcome</th>
                    <th className="px-2.5 py-1.5 text-end text-[11px] font-semibold tracking-[.03em] text-[color:var(--muted)]">P&amp;L</th>
                  </tr>
                </thead>
                <tbody>
                  {aiRecs.map((r) => (
                    <tr key={r.id} className={`border-t ${borderClass} hover:bg-[color:var(--mmec-row-hover)]`}>
                      <td className={`px-2.5 py-1.5 whitespace-nowrap ${textClass}`}>{r.ts ? String(r.ts).slice(0, 19).replace('T', ' ') : '—'}</td>
                      <td className={`px-2.5 py-1.5 font-medium ${textClass}`}>{r.symbol}</td>
                      <td className={`px-2.5 py-1.5 ${textClass}`}>{r.strategy_type}</td>
                      <td className={`px-2.5 py-1.5 max-w-[140px] truncate ${textClass}`} title={r.target}>{r.target || '—'}</td>
                      <td className={`px-2.5 py-1.5 max-w-[140px] truncate ${textClass}`} title={r.stop}>{r.stop || '—'}</td>
                      <td className={`px-2.5 py-1.5 ${textClass}`}>{r.outcome ?? '—'}</td>
                      <td className={`px-2.5 py-1.5 text-end tabular-nums ${textClass}`}>
                        {r.pnl != null && !Number.isNaN(Number(r.pnl)) ? Number(r.pnl).toFixed(2) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!aiLoading && aiRecs.length === 0 && !aiError && (
              <div className="text-center py-8 text-[13px] text-[color:var(--muted)]">{translations.aiStrategiesEmpty}</div>
            )}
          </div>
        )}

        {activeTab === 'wallet' && (
          <div className="space-y-4">
            <div
              className="p-4 rounded-[10px] border border-[color:var(--hair)] bg-[color:var(--elev)] tcs-panel"
            >
              <h3 className={`text-[14px] font-semibold mb-2 ${textClass}`}>{translations.virtualPortfolio}</h3>
              <p className={`text-xs mb-4 text-[color:var(--muted)]`}>
                {translations.capitalHint}
              </p>
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                  <label className={`block text-xs mb-1 ${textClass}`}>{translations.startingCapital}</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min={100}
                      step={1000}
                      value={startingCapitalInput}
                      onChange={(e) => setStartingCapitalInput(e.target.value)}
                      className={`w-36 px-2 py-1 rounded border text-sm ${borderClass} bg-[color:var(--elev)] text-[color:var(--txt)]`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const n = Math.round(Number(startingCapitalInput));
                        if (Number.isFinite(n) && n >= 100) setVirtualStartingCapital(n);
                      }}
                      className="px-3 py-1 rounded text-[13px] font-medium tcs-btn"
                      style={ds ? undefined : { background: 'var(--acc)', color: 'var(--void)' }}
                    >
                      {translations.applyCapital}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-6 items-center">
                  <div>
                    <div className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[color:var(--muted-2)]">{translations.currentEquity}</div>
                    <div className={`text-[22px] font-semibold tabular-nums ${textClass}`} style={{ fontFamily: 'var(--font-mono)' }}>${wallet.balance.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[color:var(--muted-2)]">{translations.totalReturn}</div>
                    <div className={`text-[22px] font-semibold tabular-nums ${getPnLColor(portfolioMetrics.totalReturnPct)}`} style={{ fontFamily: 'var(--font-mono)' }}>
                      {portfolioMetrics.totalReturnPct >= 0 ? '+' : ''}
                      {portfolioMetrics.totalReturnPct.toFixed(2)}%
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[color:var(--muted-2)]">{translations.totalPnL}</div>
                  <div className={`text-[20px] font-semibold tabular-nums ${getPnLColor(wallet.totalPnL)}`} style={{ fontFamily: 'var(--font-mono)' }}>
                    ${wallet.totalPnL.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[color:var(--muted-2)]">{translations.unrealized}</div>
                  <div className={`text-[20px] font-semibold tabular-nums ${getPnLColor(portfolioMetrics.unrealized)}`} style={{ fontFamily: 'var(--font-mono)' }}>
                    ${portfolioMetrics.unrealized.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[color:var(--muted-2)]">{translations.realized}</div>
                  <div className={`text-[20px] font-semibold tabular-nums ${getPnLColor(portfolioMetrics.realized)}`} style={{ fontFamily: 'var(--font-mono)' }}>
                    ${portfolioMetrics.realized.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[color:var(--muted-2)]">{translations.winRate}</div>
                  <div className={`text-[20px] font-semibold tabular-nums ${textClass}`} style={{ fontFamily: 'var(--font-mono)' }}>{wallet.performance.winRate.toFixed(1)}%</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <div className="text-xs text-[color:var(--muted)] mb-1">{translations.equityCurve}</div>
                  <EquitySparkline
                    history={wallet.equityHistory || [{ equity: wallet.balance }]}
                    stroke={MMEC_BRAND_COLORS.blue}
                  />
                </div>
                <div className="text-xs text-[color:var(--muted)]">{translations.totalTrades}: {wallet.performance.totalTrades}</div>
              </div>
            </div>

            {/* Export/Import / Reset */}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleExport}
                className="px-3 py-1.5 rounded-lg text-[13px] font-medium bg-[color:var(--up)] text-[color:var(--void)] transition-opacity hover:opacity-90 tcs-btn"
              >
                📤 {translations.export}
              </button>
              <label className="px-3 py-1.5 rounded-lg text-[13px] font-medium bg-[color:var(--acc)] text-[color:var(--void)] transition-opacity hover:opacity-90 cursor-pointer tcs-btn">
                📥 {translations.import}
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                />
              </label>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(translations.resetPaperConfirm)) resetPaperWallet();
                }}
                className="mmec-btn"
              >
                {translations.resetPaper}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="space-y-4">
            {/* Performance Metrics */}
            <div className={`p-4 rounded-lg bg-[color:var(--elev2)] tcs-panel`}>
              <h3 className={`text-[14px] font-semibold mb-3 ${textClass}`}>{translations.performance}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[color:var(--muted-2)]">Win Rate</div>
                  <div className={`text-[20px] font-semibold tabular-nums ${textClass}`} style={{ fontFamily: 'var(--font-mono)' }}>
                    {wallet.performance.winRate.toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[color:var(--muted-2)]">Profit Factor</div>
                  <div className={`text-[20px] font-semibold tabular-nums ${textClass}`} style={{ fontFamily: 'var(--font-mono)' }}>
                    {wallet.performance.profitFactor.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[color:var(--muted-2)]">Max Drawdown</div>
                  <div className="text-[20px] font-semibold tabular-nums text-[color:var(--down)]" style={{ fontFamily: 'var(--font-mono)' }}>
                    {wallet.performance.maxDrawdown.toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[color:var(--muted-2)]">Avg Win</div>
                  <div className="text-[20px] font-semibold tabular-nums text-[color:var(--up)]" style={{ fontFamily: 'var(--font-mono)' }}>
                    ${wallet.performance.averageWin.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[color:var(--muted-2)]">Avg Loss</div>
                  <div className="text-[20px] font-semibold tabular-nums text-[color:var(--down)]" style={{ fontFamily: 'var(--font-mono)' }}>
                    ${wallet.performance.averageLoss.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[color:var(--muted-2)]">Sharpe Ratio</div>
                  <div className={`text-[20px] font-semibold tabular-nums ${textClass}`} style={{ fontFamily: 'var(--font-mono)' }}>
                    {wallet.performance.sharpeRatio.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingCallSystem; 